/**
 * Appelant inconnu : proposition de fiche contact, avec rattachement facultatif
 * à une entreprise.
 *
 * Auparavant une fiche vide était créée en silence dès qu'un numéro inconnu
 * décrochait — des contacts « Nouveau contact » sans nom s'accumulaient, et le
 * rattachement à l'entreprise devait être refait à la main plus tard. Ici le
 * commercial saisit ce qu'il a sous la main pendant l'appel, et rattache
 * l'entreprise dans le même geste s'il la reconnaît.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Building2, Loader2, Plus, Search, UserPlus, X } from "lucide-react";
import api, { apiError } from "@/lib/crmApi";
import { toast } from "sonner";
import { StatusBadge } from "@/crm/ui";
import Modal from "@/crm/components/Modal";

const CHAMP = "w-full px-3 py-2 rounded-lg border border-slate-300 text-sm outline-none focus:border-indigo-500";

export default function UnknownCallerModal({ phone, onClose, onCreated }) {
  const [form, setForm] = useState({ prenom: "", nom: "", fonction: "", email: "" });
  const [recherche, setRecherche] = useState("");
  const [resultats, setResultats] = useState([]);
  const [entreprise, setEntreprise] = useState(null);
  const [enCours, setEnCours] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    clearTimeout(timer.current);
    const q = recherche.trim();
    if (entreprise || q.length < 2) { setResultats([]); return; }
    timer.current = setTimeout(() => {
      api.get(`/companies?search=${encodeURIComponent(q)}&limit=6`)
        .then(({ data }) => setResultats(data.items || []))
        .catch(() => setResultats([]));
    }, 300);
    return () => clearTimeout(timer.current);
  }, [recherche, entreprise]);

  // L'entreprise peut ne pas encore exister au CRM : on la crée et on la lie
  // dans le même geste, plutôt que d'obliger à sortir de l'appel.
  const creerEntreprise = useCallback(async () => {
    const nom = recherche.trim();
    if (!nom) return;
    try {
      const { data } = await api.post("/companies", { nom, source: "appel_entrant" });
      setEntreprise(data);
      setResultats([]);
      toast.success(`Entreprise « ${data.nom} » créée`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Création de l'entreprise impossible");
    }
  }, [recherche]);

  const creer = useCallback(async () => {
    setEnCours(true);
    try {
      const { data } = await api.post("/contacts", {
        ...form,
        nom: form.nom.trim() || `Appelant ${phone}`,
        telephone: phone,
        company_id: entreprise?.id || null,
        source: "appel_entrant",
      });
      toast.success(entreprise
        ? `Fiche créée et rattachée à ${entreprise.nom}`
        : "Fiche contact créée");
      onCreated?.(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Création impossible");
    } finally { setEnCours(false); }
  }, [form, phone, entreprise, onCreated]);

  return (
    <Modal onClose={onClose} zClassName="z-[70]" padded={false} className="shadow-xl"
      testid="unknown-caller-modal" label="Numéro inconnu">
      <div className="flex items-start gap-3 px-5 py-4 border-b border-slate-100">
        <span className="h-9 w-9 shrink-0 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
          <UserPlus size={17} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="font-heading font-bold text-slate-900">Numéro inconnu</div>
          <div className="font-mono text-sm text-slate-500">{phone}</div>
        </div>
        <button onClick={onClose} className="p-1 rounded-md text-slate-400 hover:bg-slate-100"><X size={16} /></button>
      </div>

      <div className="p-5 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <input data-testid="caller-prenom" placeholder="Prénom" className={CHAMP} autoFocus
            value={form.prenom} onChange={(e) => setForm((f) => ({ ...f, prenom: e.target.value }))} />
          <input data-testid="caller-nom" placeholder="Nom" className={CHAMP}
            value={form.nom} onChange={(e) => setForm((f) => ({ ...f, nom: e.target.value }))} />
        </div>
        <input data-testid="caller-fonction" placeholder="Fonction" className={CHAMP}
          value={form.fonction} onChange={(e) => setForm((f) => ({ ...f, fonction: e.target.value }))} />
        <input data-testid="caller-email" placeholder="Email" className={CHAMP}
          value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />

        <div>
          <label className="text-xs text-slate-500">Rattacher à une entreprise (facultatif)</label>
          {entreprise ? (
            <div className="mt-1 flex items-center gap-2 px-3 py-2 rounded-lg border border-indigo-200 bg-indigo-50/60"
              data-testid="caller-company-linked">
              <Building2 size={15} className="text-indigo-600 shrink-0" />
              <span className="text-sm text-slate-800 truncate flex-1">{entreprise.nom}</span>
              {entreprise.status && <StatusBadge status={entreprise.status} />}
              <button onClick={() => { setEntreprise(null); setRecherche(""); }}
                className="text-xs text-slate-500 hover:text-slate-800 shrink-0">Changer</button>
            </div>
          ) : (
            <div className="relative mt-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input data-testid="caller-company-search" placeholder="Nom, SIRET, ville…"
                className={`${CHAMP} pl-9`} value={recherche}
                onChange={(e) => setRecherche(e.target.value)} />
              {recherche.trim().length >= 2 && (
                <button data-testid="caller-create-company" onClick={creerEntreprise}
                  className="mt-1.5 flex items-center gap-1.5 text-[12.5px] font-medium text-indigo-600 hover:text-indigo-700">
                  <Plus size={13} /> Créer l'entreprise « {recherche.trim()} »
                </button>
              )}
              {resultats.length > 0 && (
                <ul className="absolute z-10 left-0 right-0 mt-1 max-h-44 overflow-auto rounded-xl border border-slate-200 bg-white shadow-lg py-1">
                  {resultats.map((c) => (
                    <li key={c.id}>
                      <button data-testid="caller-company-option" onClick={() => setEntreprise(c)}
                        className="w-full text-left px-3 py-2 hover:bg-slate-50">
                        <span className="block text-sm text-slate-800 truncate">{c.nom}</span>
                        <span className="block text-[11.5px] text-slate-400 truncate">
                          {[c.ville, c.code_postal, c.siren].filter(Boolean).join(" · ")}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-2 px-5 py-4 bg-slate-50 border-t border-slate-100">
        <button onClick={onClose} data-testid="caller-skip"
          className="px-4 py-2 rounded-xl border border-slate-300 text-sm text-slate-600">Plus tard</button>
        <button onClick={creer} disabled={enCours} data-testid="caller-create"
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-60">
          {enCours ? <Loader2 size={15} className="animate-spin" /> : <UserPlus size={15} />}
          Créer la fiche
        </button>
      </div>
    </Modal>
  );
}
